import { Link } from "react-router-dom";
import { getLifecycleStageColor, getFunnelStageColor, isActiveWipStage, DASHBOARD_COLORS } from "@/utils/dashboardStageColors";
import { getLifecycleStageIcon, getFunnelStageIcon } from "@/utils/dashboardIcons";
import { getBlockedReasonBadge } from "@/utils/dashboardColors";

// --- Case lifecycle horizontal bars ---------------------------------------
export function CaseLifecycleCard({ data }) {
  if (!data) return null;
  const stages = data.stages || [];
  const max = stages.reduce((m, s) => Math.max(m, s.count), 0) || 1;
  return (
    <div className="co-card p-slate p-5" data-testid="lifecycle-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="font-bold text-slate-900">Case lifecycle · live distribution</div>
          <div className="text-xs text-slate-500">Where every active case currently sits · click a stage to filter</div>
        </div>
      </div>
      {stages.length === 0 ? <div className="text-sm text-slate-500 py-8">No active cases in this lifecycle stage.</div> : (
        <div className="space-y-2.5">
          {stages.map((s) => {
            const color = getLifecycleStageColor(s);
            const active = isActiveWipStage(s);
            const Icon = getLifecycleStageIcon(s.key || s.label);
            // Append drill-down context so destinations can render filter chips.
            const base = s.targetUrl || "#";
            const joiner = base.includes("?") ? "&" : "?";
            const href = base === "#"
              ? "#"
              : `${base}${joiner}sourceTile=lifecycle-stage-${encodeURIComponent(s.key || (s.label || "").toLowerCase())}&sourceDashboard=main`;
            return (
              <Link key={s.key} to={href}
                    className="grid grid-cols-[130px_1fr_60px_56px] items-center gap-3 group p-1 -mx-1 rounded-md transition hover:bg-white/60"
                    data-testid={`stage-${s.key}`}>
                <div className="text-sm text-slate-700 flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
                  <span className="truncate">{s.label}</span>
                </div>
                <div className="rounded-md overflow-hidden" style={{ background: DASHBOARD_COLORS.track, height: active ? 28 : 24 }}>
                  <div className="h-full rounded-md transition group-hover:opacity-90"
                       style={{ width: `${(s.count / max) * 100}%`, background: color, boxShadow: active ? `inset 0 -2px 0 rgba(0,0,0,0.08)` : "none" }} />
                </div>
                <div className="text-sm font-semibold text-slate-800 text-right">{s.count}</div>
                <div className="text-xs text-slate-500 text-right">{s.percent}%</div>
              </Link>
            );
          })}
        </div>
      )}
      <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
        <MiniMetric label="Lead → Won" value={data.leadToWonPercent} suffix="%" />
        <TatMetric label="Avg case TAT" days={data.avgCaseTatDays} />
        <MiniMetric label="First-pass approval" value={data.firstPassApprovalPercent} suffix="%" />
      </div>
    </div>
  );
}

function TatMetric({ label, days }) {
  if (days == null) return <MiniMetric label={label} value={null} />;
  // Sub-day → show hours; under a week → days; else weeks
  let display;
  if (days < 1) display = `${Math.round(days * 24)}h`;
  else if (days < 7) display = `${days}d`;
  else display = `${(days / 7).toFixed(1)}w`;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }} data-testid="tat-value">{display}</div>
    </div>
  );
}

function MiniMetric({ label, value, suffix = "" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>
        {value == null ? "—" : `${value}${suffix}`}
      </div>
    </div>
  );
}

// --- Risk & SLA ------------------------------------------------------------
export function RiskSlaCard({ data }) {
  if (!data) return null;
  return (
    <div className="co-card p-rose p-5" data-testid="risk-sla-card">
      <div className="flex items-center justify-between mb-3">
        <div className="font-bold text-slate-900">Risk &amp; SLA</div>
        {data.breachedCount > 0 ? (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-600" data-testid="risk-breached">{data.breachedCount} breached</span>
        ) : (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">no breach</span>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Case aging · days in current stage</div>
      {data.agingBuckets?.length ? (
        <div className="grid grid-cols-4 gap-2 mb-4">
          {data.agingBuckets.map((b) => (
            <div key={b.label} className="text-center" data-testid={`aging-bucket-${b.label}`}>
              <div className="text-xl font-bold text-slate-800">{b.count}</div>
              <div className="text-[10px] text-slate-500 uppercase">{b.label}</div>
            </div>
          ))}
        </div>
      ) : <div className="text-sm text-slate-500 mb-4">No open cases to age.</div>}

      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Blocked · by reason</div>
      {data.blockedReasons?.length ? (
        <div className="space-y-1.5">
          {data.blockedReasons.map((r) => {
            // 2026-02-27 executive-color spec — chip colour reflects the REASON,
            // not the underlying severity bucket.
            const badge = getBlockedReasonBadge(r.reason);
            return (
              <div key={r.reason} className="flex items-center justify-between text-sm" data-testid={`blocked-${r.reason.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.chip}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                    {r.reason}
                  </span>
                </div>
                <div className="font-semibold text-slate-800">{r.count}</div>
              </div>
            );
          })}
        </div>
      ) : <div className="text-sm text-slate-500">No blocked cases.</div>}

      <Link
        to={`${(data.riskRegisterUrl || "/cases?filter=at_risk")}${(data.riskRegisterUrl || "").includes("?") ? "&" : "?"}sourceTile=risk-register&sourceDashboard=main`.replace(/\?\?/, "?")}
        className="inline-flex items-center gap-1 mt-4 text-[11px] font-semibold px-3 py-1 rounded-full border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-400 transition"
        data-testid="risk-register-link">
        Open risk register →
      </Link>
    </div>
  );
}

// --- Renewals 90-day horizon ----------------------------------------------
export function RenewalsHorizonCard({ data }) {
  if (!data) return null;
  const buckets = data.weeklyBuckets || [];
  const values = buckets.map((b) => Number(b.value) || 0);
  const maxValue = values.reduce((m, v) => Math.max(m, v), 0);
  const chartMax = maxValue > 0 ? maxValue : 1;
  const CHART_HEIGHT = 160;     // px — readable column area
  const MIN_VISIBLE = 6;        // px — non-zero bars always render
  const TRACK_PAD = 4;          // px — keeps tallest bar away from top edge
  const colorMap = { "0-30d": "bg-rose-500", "31-60d": "bg-amber-500", "61-90d": "bg-sky-500" };
  return (
    <div className="co-card p-lav p-5" data-testid="renewals-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-bold text-slate-900">Renewals · 90-day horizon</div>
          <div className="text-xs text-slate-500">Value at risk · grouped by window</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total at risk</div>
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }} data-testid="renewals-total">{data.formattedTotalAtRisk}</div>
        </div>
      </div>
      <div className="flex items-center gap-4 text-[10px] uppercase font-bold text-slate-500 mb-2">
        <Legend color="bg-rose-500" label="0-30d" />
        <Legend color="bg-amber-500" label="31-60d" />
        <Legend color="bg-sky-500" label="61-90d" />
      </div>
      {maxValue > 0 ? (
        <>
          <div
            className="flex items-end gap-1 mb-1"
            style={{ height: CHART_HEIGHT }}
            data-testid="renewals-chart"
          >
            {buckets.map((b) => {
              const v = Number(b.value) || 0;
              const ratio = v / chartMax;
              const barHeight = v > 0 ? Math.max(ratio * (CHART_HEIGHT - TRACK_PAD), MIN_VISIBLE) : 0;
              return (
                <div
                  key={b.weekLabel}
                  className={`flex-1 rounded-t ${colorMap[b.window] || "bg-slate-300"}`}
                  style={{ height: barHeight }}
                  title={`${b.weekLabel}: ${b.formattedValue}`}
                  data-testid={`renewals-bar-${b.weekLabel}`}
                />
              );
            })}
          </div>
          <div className="flex gap-1 mb-2">
            {buckets.map((b) => (
              <div key={`lbl-${b.weekLabel}`} className="flex-1 text-[9px] text-slate-500 text-center">{b.weekLabel}</div>
            ))}
          </div>
        </>
      ) : <div className="text-sm text-slate-500 py-8 text-center">No renewals due in the next 90 days.</div>}

      <div className="mt-3 space-y-2 max-h-44 overflow-y-auto">
        {(data.urgentList || []).map((r) => (
          <Link to={r.targetUrl} key={r.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`urgent-renewal-${r.id}`}>
            <div className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.daysRemaining <= 30 ? "bg-rose-100 text-rose-700" : r.daysRemaining <= 60 ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>{r.daysRemaining}d</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">{r.renewalNumber} · {r.customerName}</div>
              <div className="text-[11px] text-slate-500 truncate">{r.serviceName} · {r.formattedExpectedValue}</div>
            </div>
            <div className="text-[11px] font-semibold text-cyan-700">Send notice →</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return <span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-sm ${color}`} />{label}</span>;
}

// --- Sales funnel ----------------------------------------------------------
export function SalesFunnelCard({ data }) {
  if (!data) return null;
  const stages = data.stages || [];
  const max = stages.reduce((m, s) => Math.max(m, s.count), 0) || 1;
  return (
    <div className="co-card p-mint p-5" data-testid="funnel-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-bold text-slate-900">Sales funnel · {data.stages?.length ? "live" : "—"}</div>
          <div className="text-xs text-slate-500">Volume + conversion at each gate</div>
        </div>
      </div>
      {stages.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center">No leads in this period.</div> : (
        <div className="space-y-2">
          {stages.map((s) => {
            const color = getFunnelStageColor(s);
            const Icon = getFunnelStageIcon(s.key || s.label);
            const base = s.targetUrl || "#";
            const joiner = base.includes("?") ? "&" : "?";
            const href = base === "#"
              ? "#"
              : `${base}${joiner}sourceTile=funnel-stage-${encodeURIComponent(s.key || (s.label || "").toLowerCase())}&sourceDashboard=main`;
            return (
              <Link key={s.key} to={href}
                    className="grid grid-cols-[80px_1fr_90px] items-center gap-3 p-1 -mx-1 rounded-md transition hover:bg-white/60"
                    data-testid={`funnel-${s.key}`}>
                <div className="text-sm text-slate-700 flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
                  <span className="truncate">{s.label}</span>
                </div>
                <div className="h-5 rounded-md overflow-hidden" style={{ background: DASHBOARD_COLORS.track }}>
                  <div className="h-full rounded-md transition" style={{ width: `${(s.count / max) * 100}%`, background: color }} />
                </div>
                <div className="text-xs text-right text-slate-600">
                  <span className="font-semibold text-slate-800">{s.count}</span>
                  {s.conversionPercent != null ? <span className="text-slate-500"> · {s.conversionPercent}%</span> : null}
                </div>
              </Link>
            );
          })}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Win Rate</div>
          <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>{data.winRatePercent == null ? "—" : `${data.winRatePercent}%`}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Avg Deal</div>
          <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>{data.formattedAvgDealAmount || "—"}</div>
        </div>
      </div>
    </div>
  );
}

// --- Activity stream -------------------------------------------------------
export function ActivityStreamCard({ items }) {
  const sev = { critical: "bg-rose-500", warning: "bg-amber-500", success: "bg-emerald-500", info: "bg-slate-400" };
  return (
    <div className="co-card p-slate p-5" data-testid="activity-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-bold text-slate-900">Activity stream</div>
          <div className="text-xs text-slate-500">Audit-grade · all critical writes &amp; overrides</div>
        </div>
      </div>
      {!items?.length ? <div className="text-sm text-slate-500 py-6 text-center">No recent activity yet.</div> : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {items.map((a) => (
            <div key={a.id} className="flex items-start gap-3" data-testid={`activity-${a.id}`}>
              <span className={`w-2 h-2 mt-1.5 rounded-full ${sev[a.severity] || "bg-slate-400"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800 flex items-center gap-2">
                  <span className="truncate">{a.message}</span>
                  {a.tag ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">{a.tag}</span> : null}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{a.actorName || "system"} · {formatTime(a.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 60000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.round(diff)} min ago`;
  if (diff < 60 * 24) return `${Math.round(diff / 60)} h ago`;
  if (diff < 60 * 24 * 7) return `${Math.round(diff / 60 / 24)} d ago`;
  return d.toISOString().slice(0, 10);
}

// --- Team workload ---------------------------------------------------------
// 2026-06 · stacked stage breakdown. Stage colours are assigned from the
// shared legend order so a stage keeps the same colour for every member.
const STAGE_COLORS = [
  "#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444",
  "#14b8a6", "#ec4899", "#6366f1", "#84cc16", "#f97316",
  "#64748b", "#06b6d4",
];

export function TeamWorkloadCard({ items, recommendations, stages }) {
  const legend = (stages?.length ? stages : []).map((s, i) => ({ ...s, color: STAGE_COLORS[i % STAGE_COLORS.length] }));
  const colorFor = (key) => legend.find((s) => s.stageKey === key)?.color || "#94a3b8";
  return (
    <div className="co-card p-peach p-5" data-testid="workload-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-bold text-slate-900">Team workload</div>
          <div className="text-xs text-slate-500">Active cases per owner · capacity signal</div>
        </div>
        <Link to="/cases?groupBy=owner&taskStatus=open&sourceTile=team-workload&sourceDashboard=main"
              className="text-[11px] font-semibold text-cyan-700 px-3 py-1 rounded-full border border-cyan-200 bg-white/60 hover:bg-cyan-50 hover:border-cyan-400 transition"
              data-testid="workload-view-all">
          View all workload →
        </Link>
      </div>
      {legend.length ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3" data-testid="workload-stage-legend">
          {legend.map((s) => (
            <span key={s.stageKey} className="flex items-center gap-1.5 text-[11px] text-slate-600"
                  data-testid={`workload-legend-${s.stageKey}`}
                  title={`${s.stageLabel} · ${s.totalCases} case${s.totalCases === 1 ? "" : "s"}`}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              <span className="truncate max-w-[150px]">{s.stageLabel}</span>
            </span>
          ))}
        </div>
      ) : null}
      {!items?.length ? <div className="text-sm text-slate-500 py-6 text-center">No workload data available.</div> : (
        <div className="space-y-2">
          {items.map((u) => {
            const pct = Math.min(u.utilizationPercent, 200);
            const segments = u.stageBreakdown || [];
            const segTotal = segments.reduce((s, x) => s + (x.count || 0), 0);
            const barWidth = Math.min(pct, 100);
            // 2026-06 · striped tail = the share of the workload that sits
            // above capacity. Total bar width logic is unchanged.
            const overflow = Math.max((u.activeCases || 0) - (u.maxCapacity || 0), 0);
            const tailPct = overflow > 0 && u.activeCases ? (overflow / u.activeCases) * 100 : 0;
            const bar = u.isOverCapacity ? "bg-rose-500" : u.utilizationPercent > 80 ? "bg-amber-500" : "bg-sky-500";
            return (
              <Link key={u.userId}
                    to={`/cases?assignee=${encodeURIComponent(u.userId)}&sourceTile=team-workload&sourceDashboard=main`}
                    className="block text-sm p-2 -mx-2 rounded-md hover:bg-white/60 transition"
                    title={`View cases assigned to ${u.userName}`}
                    data-testid={`workload-${u.userId}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-700 truncate">{u.userName}</span>
                  <span className={`font-mono text-xs ${u.isOverCapacity ? "text-rose-600 font-bold" : "text-slate-600"}`}>{u.activeCases} / {u.maxCapacity}{u.isOverCapacity ? " ⚠" : ""}</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full flex" style={{ width: `${barWidth}%` }} data-testid={`workload-bar-${u.userId}`}>
                    {segTotal > 0 ? segments.map((s) => (
                      <div key={s.stageKey}
                           className="h-full transition-opacity hover:opacity-80"
                           style={{ width: `${(s.count / segTotal) * (100 - tailPct)}%`, backgroundColor: colorFor(s.stageKey) }}
                           title={`${s.stageLabel}: ${s.count} case${s.count === 1 ? "" : "s"}`}
                           data-testid={`workload-seg-${u.userId}-${s.stageKey}`} />
                    )) : <div className={`h-full ${bar}`} style={{ width: `${100 - tailPct}%` }} />}
                    {tailPct > 0 ? (
                      <div className="h-full transition-opacity hover:opacity-80"
                           style={{
                             width: `${tailPct}%`,
                             backgroundImage: "repeating-linear-gradient(45deg, #e11d48 0 3px, #fecdd3 3px 6px)",
                           }}
                           title={`Over capacity: ${overflow} case${overflow === 1 ? "" : "s"} above the limit of ${u.maxCapacity} (${u.activeCases} active)`}
                           data-testid={`workload-overflow-${u.userId}`} />
                    ) : null}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      {recommendations?.length ? (
        <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900" data-testid="workload-recommendation">
          {recommendations.map((r, i) => <div key={i}>⚠ {r.message}</div>)}
        </div>
      ) : null}
    </div>
  );
}

// =========================================================================
// Sprint DASH-CMD-CENTRE (2026-09-06)
// =========================================================================

// Panel 4 · Where work-time is trapped ------------------------------------
// Ranked horizontal-bar list on queueCostDays (WIP × P50). Reads
// `data.stageBottlenecks = {rows[], bottleneckStage, casesAnalysed}`
// emitted by dashboard_owner_v2._risk_sla via stage_dwell.stage_cycle_stats.
// Degrades cleanly when the pack builder returns empty rows.
export function WhereWorkTimeIsTrappedCard({ data }) {
  const rows = data?.rows || [];
  const maxCost = rows.reduce((m, r) => Math.max(m, r.queueCostDays || 0), 0) || 1;
  return (
    <div className="co-card p-slate p-5" data-testid="where-work-time-trapped-card">
      <div className="flex items-center justify-between mb-1">
        <div className="font-bold text-slate-900">Where work-time is trapped</div>
        {data?.bottleneckStage ? (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700"
                data-testid="stage-bottleneck-badge">
            Bottleneck: {(data.bottleneckStage || "").replace(/_/g, " ")}
          </span>
        ) : null}
      </div>
      <div className="text-[11px] text-slate-500 mb-3">cases in stage × median dwell</div>
      {rows.length ? (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.stageKey}
                 className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 text-[12px]"
                 data-testid={`stage-bottleneck-row-${r.stageKey}`}
                 title={`${r.wipCount || 0} WIP × p50 ${r.p50DwellDays ?? "?"}d`}>
              <div className="min-w-0">
                <div className="truncate text-slate-800 font-semibold">{r.stageLabel}</div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1">
                  <div className="h-full bg-slate-500"
                       style={{ width: `${Math.min(100, ((r.queueCostDays || 0) / maxCost) * 100)}%` }} />
                </div>
              </div>
              <div className="text-right whitespace-nowrap text-slate-700">
                <div className="font-semibold" data-testid={`stage-cost-${r.stageKey}`}>
                  {Math.round(r.queueCostDays || 0)} d
                </div>
                <div className="text-[10px] text-slate-500">
                  wip {r.wipCount || 0} · p50 {r.p50DwellDays ?? "—"} · p80 {r.p80DwellDays ?? "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-slate-500" data-testid="stage-bottleneck-empty">
          No open-stage dwell recorded yet. The stage-dwell backfill populates this
          panel once cases move between stages.
        </div>
      )}
      {data?.casesAnalysed ? (
        <div className="mt-3 text-[10px] text-slate-500">
          Aggregate across {data.casesAnalysed} cases · a bottleneck is a property of the queue, not of one person.
        </div>
      ) : null}
    </div>
  );
}


// Panel 5 · First-pass yield by authority ---------------------------------
// Worst-first authority table with verdict chips. Reads
// `data.firstPassYieldByAuthority = {authorities[], overall, submissionsDecided,
//  reworkRatePercent}`. Denominator is decided submissions only — the number
// will typically move UPWARD from the legacy all-submission rate. That is the
// fix, not a bug.
const _VERDICT_STYLE = {
  PRE_REVIEW_NEEDED: { chip: "bg-rose-50 text-rose-700",    label: "Pre-review" },
  SLOW_RESPONDER:    { chip: "bg-amber-50 text-amber-700",  label: "Slow" },
  OK:                { chip: "bg-emerald-50 text-emerald-700", label: "OK" },
};
export function FirstPassYieldByAuthorityCard({ data, legacyOverall }) {
  const rows = [...(data?.authorities || [])].sort(
    (a, b) => (a.firstPassYieldPercent ?? 100) - (b.firstPassYieldPercent ?? 100)
  );
  const overall = data?.overall;
  const decided = data?.submissionsDecided || 0;
  return (
    <div className="co-card p-slate p-5" data-testid="fpy-by-authority-card">
      <div className="flex items-baseline justify-between mb-1">
        <div className="font-bold text-slate-900">First-pass yield · by authority</div>
        <div className="text-[11px] text-slate-500" data-testid="fpy-decided-denominator">
          {decided.toLocaleString()} decided
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        Rate = round-1 approvals ÷ decided submissions. Legacy all-submission rate: {legacyOverall ?? "—"}%.
      </div>
      {overall !== null && overall !== undefined ? (
        <div className="mb-3 flex items-baseline gap-2">
          <div className="text-3xl font-bold text-slate-900" data-testid="fpy-overall-pct">
            {overall}%
          </div>
          {data?.reworkRatePercent !== null && data?.reworkRatePercent !== undefined ? (
            <div className="text-[11px] text-slate-500">rework {data.reworkRatePercent}%</div>
          ) : null}
        </div>
      ) : (
        <div className="text-sm text-slate-500 mb-3" data-testid="fpy-overall-empty">
          No decided submissions yet — rate appears here once authorities return verdicts.
        </div>
      )}
      {rows.length ? (
        <div className="space-y-1.5">
          {rows.slice(0, 6).map((a) => {
            const v = _VERDICT_STYLE[a.verdict] || _VERDICT_STYLE.OK;
            return (
              <div key={a.authorityId || a.authorityName}
                   className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 text-[12px]"
                   data-testid={`fpy-row-${a.authorityId || a.authorityName}`}>
                <div className="truncate text-slate-800 font-semibold" title={a.authorityName}>
                  {a.authorityName}
                </div>
                <div className="text-right whitespace-nowrap">
                  <span className="font-bold text-slate-800" data-testid={`fpy-pct-${a.authorityId || a.authorityName}`}>
                    {a.firstPassYieldPercent ?? "—"}%
                  </span>
                  <span className="text-[10px] text-slate-500 ml-1">
                    · {a.decided || 0} · p80 {a.p80ResponseDays ?? "—"}d
                  </span>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${v.chip}`}>
                  {v.label}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}


// Panel 6 · Case risk register --------------------------------------------
// Full table (not a card grid — comparison across rows is the point).
// Reads `data.riskRegister = {rows[], totalAtRisk, criticalCount,
// overdueArOnAtRiskCases, scoringNote, targetUrl}`.
const _RISK_STRIPE = {
  CRITICAL: "#E11D48", HIGH: "#F59E0B", MEDIUM: "#64748B", LOW: "#94A3B8",
};
export function CaseRiskRegisterCard({ data }) {
  if (!data) return null;
  const rows = data.rows || [];
  if (!rows.length) {
    return (
      <div className="co-card p-slate p-5" data-testid="risk-register-card">
        <div className="font-bold text-slate-900 mb-1">Case risk register</div>
        <div className="text-sm text-slate-500" data-testid="risk-register-empty">
          No at-risk cases right now. The register scores every open case on
          overdue AR, stage dwell and outstanding documents.
        </div>
      </div>
    );
  }
  return (
    <div className="co-card p-slate p-5" data-testid="risk-register-card">
      <div className="flex items-baseline justify-between mb-1">
        <div className="font-bold text-slate-900">Case risk register</div>
        <div className="flex items-center gap-2 text-[11px]">
          {data.criticalCount ? (
            <span className="font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700"
                  data-testid="risk-critical-count">
              {data.criticalCount} critical
            </span>
          ) : null}
          <span className="text-slate-500">{data.totalAtRisk || rows.length} at-risk</span>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        Composite score: overdue AR + stage dwell + open documents.
      </div>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-[12px]" data-testid="risk-register-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <th className="py-2 px-2">Case</th>
              <th className="py-2 px-2">Customer</th>
              <th className="py-2 px-2">Stage</th>
              <th className="py-2 px-2 text-right">Days</th>
              <th className="py-2 px-2 text-right">Overdue AR</th>
              <th className="py-2 px-2 text-right">Score</th>
              <th className="py-2 px-2">Why</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.case_number}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  style={{ boxShadow: `inset 3px 0 0 ${_RISK_STRIPE[r.risk_level] || "#94A3B8"}` }}
                  data-testid={`risk-row-${r.case_number}`}>
                <td className="py-1.5 px-2 font-semibold text-slate-800">{r.case_number}</td>
                <td className="py-1.5 px-2 text-slate-700 truncate max-w-[160px]" title={r.customer_name}>
                  {r.customer_name}
                </td>
                <td className="py-1.5 px-2 text-slate-600 truncate max-w-[120px]" title={r.stage}>
                  {r.stage || "—"}
                </td>
                <td className="py-1.5 px-2 text-right text-slate-700">{r.days_in_stage ?? "—"}</td>
                <td className="py-1.5 px-2 text-right text-slate-700">
                  {r.overdue_ar ? Math.round(r.overdue_ar).toLocaleString() : "—"}
                </td>
                <td className="py-1.5 px-2 text-right">
                  <span className="font-bold text-slate-900">{r.risk_score}</span>
                  <span className="text-[10px] text-slate-500 ml-1">{r.risk_level}</span>
                </td>
                <td className="py-1.5 px-2 text-slate-600 text-[11px]">{r.drivers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-[10px] text-slate-500">
        <span>{data.scoringNote}</span>
        <Link to={data.targetUrl || "/analytics?report=case_risk_register"}
              className="font-semibold px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-100"
              data-testid="risk-register-view-all">
          View all →
        </Link>
      </div>
    </div>
  );
}


// Panel 3 · 13-week cash position (Slice 4) --------------------------------
// Cumulative cash line with a filled-dot marker on the first negative week,
// rendered as inline SVG in the existing Sparkline idiom (no chart library
// enters the bundle). Reads `data.cashPosition = {weeks[], firstNegativeWeek,
// netThirteenWeeks, totalInflowWeighted, totalOutflow, caveats[]}`.
// Caveats printed VERBATIM in the footer per the plan — a forecast that
// hides its exclusions is worse than none.
export function CashPositionCard({ data }) {
  if (!data) return null;
  const weeks = data.weeks || [];
  if (!weeks.length) {
    return (
      <div className="co-card p-slate p-5" data-testid="cash-position-card">
        <div className="font-bold text-slate-900 mb-1">13-week cash position</div>
        <div className="text-sm text-slate-500" data-testid="cash-position-empty">
          Forecast not yet available.
        </div>
        {(data.caveats || []).map((c, i) => (
          <div key={i} className="text-[10px] text-slate-500 mt-1"
               data-testid={`cash-position-caveat-${i}`}>· {c}</div>
        ))}
      </div>
    );
  }
  // Coordinate space: 100 x 40 viewBox for portability.
  const W = 100, H = 40, PAD_Y = 4;
  const vals = weeks.map((w) => w.cumulative || 0);
  const lo = Math.min(0, ...vals);
  const hi = Math.max(0, ...vals);
  const span = (hi - lo) || 1;
  const x = (i) => (weeks.length === 1 ? W / 2 : (i / (weeks.length - 1)) * W);
  const y = (v) => H - PAD_Y - ((v - lo) / span) * (H - 2 * PAD_Y);
  const zeroY = y(0);
  const linePts = weeks.map((w, i) => `${x(i).toFixed(2)},${y(w.cumulative).toFixed(2)}`).join(" ");
  const areaPts = `${x(0).toFixed(2)},${zeroY.toFixed(2)} ${linePts} ${x(weeks.length - 1).toFixed(2)},${zeroY.toFixed(2)}`;
  const firstNegIdx = weeks.findIndex((w) => (w.cumulative || 0) < 0);
  const positive = (data.netThirteenWeeks || 0) >= 0;
  const currency = weeks[0]?.currency || "AED";
  return (
    <div className="co-card p-slate p-5" data-testid="cash-position-card">
      <div className="flex items-baseline justify-between mb-1">
        <div className="font-bold text-slate-900">13-week cash position</div>
        {data.firstNegativeWeek ? (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700"
                data-testid="cash-position-first-negative-week">
            Runs dry: {data.firstNegativeWeek}
          </span>
        ) : (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"
                data-testid="cash-position-positive-chip">
            Positive through 13 weeks
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mb-2">
        Weekly inflow-weighted AR vs committed outflow (cumulative).
      </div>
      <div className="flex items-baseline gap-3 mb-3">
        <div className="text-3xl font-bold" data-testid="cash-position-net-13w"
             style={{ color: positive ? "#0F766E" : "#E11D48" }}>
          {positive ? "+" : ""}{Math.round(Number(data.netThirteenWeeks) || 0).toLocaleString()}
        </div>
        <div className="text-[11px] text-slate-500">
          net · in {Math.round(Number(data.totalInflowWeighted) || 0).toLocaleString()} · out {Math.round(Number(data.totalOutflow) || 0).toLocaleString()}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           className="w-full h-24 mb-2" data-testid="cash-position-sparkline">
        <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="#94A3B8" strokeWidth="0.3" strokeDasharray="1 1" />
        <polygon points={areaPts} fill={positive ? "#0F766E22" : "#E11D4822"} />
        <polyline points={linePts} fill="none"
                  stroke={positive ? "#0F766E" : "#E11D48"} strokeWidth="1.2"
                  strokeLinejoin="round" strokeLinecap="round" />
        {firstNegIdx >= 0 ? (
          <>
            <line x1={x(firstNegIdx)} y1="0" x2={x(firstNegIdx)} y2={H}
                  stroke="#E11D48" strokeWidth="0.4" strokeDasharray="1.5 1.5" />
            <circle cx={x(firstNegIdx)} cy={y(weeks[firstNegIdx].cumulative)}
                    r="1.4" fill="#E11D48" />
          </>
        ) : null}
      </svg>
      {/* NOTE: Tailwind default grid-cols-* tops out at 12; the 13-week
          layout is driven entirely by the inline `gridTemplateColumns`
          so we don't reference a bogus class here. */}
      <div className="grid gap-[1px] text-[8px] text-slate-400 mb-2"
           style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0,1fr))` }}>
        {weeks.map((w) => (
          <div key={w.week} className="text-center truncate" title={w.weekStart}>{w.week}</div>
        ))}
      </div>
      {/* Caveats — printed VERBATIM per the plan. Never truncate. */}
      <div className="border-t border-slate-100 pt-2 mt-2 space-y-0.5">
        {(data.caveats || []).map((c, i) => (
          <div key={i} className="text-[10px] text-slate-500 leading-snug"
               data-testid={`cash-position-caveat-${i}`}>· {c}</div>
        ))}
      </div>
    </div>
  );
}
